import {
  AcademicCapIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { Company, CompanySetupStepsKey } from '../../../@types/Company';
import type { AppDispatch, RootState } from '../../../store/store';
import { getValidReferents, isCompanyBrandingComplete } from '../../../utils/branding';
import classNames from '../../../utils/classNames';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import { updateSetupState } from '../../common/SetupPage';
import type { FramedSectionVisual } from './FramedSection';
import FramedSection from './FramedSection';
import SubscriptionBanner from './SubscriptionBanner';

const SETUP_TUTO_PDFS: Record<string, string> = {
  'Validation des copropriétés': '/downloads/tutoriel-1-validation-coproprietes.pdf',
  'Qualifiez vos cibles : SCI': '/downloads/tutoriel-2-qualification-sci.pdf',
  "Qualification des dates d'AG": '/downloads/tutoriel-3-qualification-dates-ag.pdf',
};

type CardStatus = 'done' | 'in-progress' | 'upcoming' | 'disabled';

type ChecklistItem = {
  label: string;
  done: boolean;
  route?: string;
};

type StepCardConfig = {
  number: number;
  title: string;
  description: string;
  checklist: ChecklistItem[];
  status: CardStatus;
  ctaRoute?: string;
  onAction?: () => void;
  onMarkAsCompleted?: () => void | Promise<void>;
  onTuto?: () => void;
};

const STATUS_HEADER_COLORS: Record<CardStatus, string> = {
  'in-progress': 'bg-orange-600',
  done: 'bg-green-700',
  upcoming: 'bg-blue-900',
  disabled: 'bg-gray-500',
};

const STATUS_CTA_COLORS: Record<CardStatus, string> = {
  'in-progress': 'bg-orange-600 hover:bg-orange-700',
  done: 'bg-green-700 hover:bg-green-800',
  upcoming: 'bg-blue-900 hover:bg-blue-950',
  disabled: '',
};

type SetupCards = {
  afaire: StepCardConfig[];
  visio: StepCardConfig[];
};

const SOLO_VISUAL: FramedSectionVisual = {
  wrapperClass: 'border-amber-100 bg-gradient-to-br from-amber-50/40 via-white to-white',
  accentBarClass: 'bg-gradient-to-b from-amber-500 to-amber-600',
  iconClass: 'bg-amber-100 text-amber-700',
  icon: <ClipboardDocumentCheckIcon className="h-5 w-5" />,
  badgeClass: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  badgeIcon: <UserIcon className="h-3 w-3" />,
  badgeLabel: 'EN AUTONOMIE',
  subtitleClass: 'text-amber-700',
};

const VISIO_VISUAL: FramedSectionVisual = {
  wrapperClass: 'border-indigo-100 bg-gradient-to-br from-indigo-50/40 via-white to-white',
  accentBarClass: 'bg-gradient-to-b from-indigo-500 to-indigo-600',
  iconClass: 'bg-indigo-100 text-indigo-700',
  icon: <VideoCameraIcon className="h-5 w-5" />,
  badgeClass: 'bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200',
  badgeIcon: <VideoCameraIcon className="h-3 w-3" />,
  badgeLabel: 'AVEC VOTRE ACCOMPAGNATEUR',
  subtitleClass: 'text-indigo-700',
};

function buildStepCards(company: Company | null): SetupCards {
  const steps = company?.setupSteps;
  const brandingComplete = isCompanyBrandingComplete(company);
  const validReferents = getValidReferents(company);
  const brandingSettings = company?.brandingSettings;

  const coproDone = !!steps?.coproValidation;
  const addCoprosDone = !!steps?.addMissingCopros;
  const addressDone = !!steps?.addressValidation;
  const step1AllDone = coproDone && addCoprosDone && addressDone;

  const transactionDone = !!steps?.transactionQualification;

  const hasCommercialName = Boolean(brandingSettings?.commercialName?.trim());
  const hasLogo = Boolean(brandingSettings?.brandingImageName?.trim());
  const hasColors = Boolean(brandingSettings?.color1?.trim()) && Boolean(brandingSettings?.color2?.trim()) && Boolean(brandingSettings?.color3?.trim());
  const hasReferent = validReferents.length > 0;
  const hasTemplate = Boolean(brandingSettings?.defaultTemplate);

  const collaboratorsDone = !!steps?.collaboratorAttribution;
  const alertsDone = !!steps?.mailAlertSetup;

  const mailConfigDone = !!steps?.mailConfigSetup;

  const sciTargetDone = !!steps?.sciTargetQualification;
  const agDateDone = !!steps?.agDateQualification;

  const reportDone = validReferents.some((referent) => Boolean(referent.pdfIntroductionName?.trim()));

  const computeStatus = (allDone: boolean, anyDone: boolean, previousComplete: boolean): CardStatus => {
    if (allDone) {return 'done';}
    if (anyDone || previousComplete) {return 'in-progress';}
    return 'upcoming';
  };

  let step1CtaRoute: string = ROUTES.ANALYZING_VERIFY_COPROPRIETES;
  if (coproDone && !addCoprosDone) {
    step1CtaRoute = ROUTES.ANALYZING_ADD_COPROPRIETES;
  } else if (coproDone && addCoprosDone && !addressDone) {
    step1CtaRoute = ROUTES.ANALYZING_ADD_ADDRESSES;
  }

  const collaboratorsCtaRoute = collaboratorsDone && !alertsDone
    ? ROUTES.SETUP_CONFIGURE_NOTIFICATIONS
    : ROUTES.SETUP_ADD_COLLABORATORS;

  const afaire: StepCardConfig[] = [
    {
      number: 1,
      title: 'Validation des copropriétés',
      description: 'Validez et complétez votre parc de copropriétés dans le RNIC et ce module.',
      checklist: [
        { label: 'Toutes vos copropriétés présentes', done: coproDone, route: ROUTES.ANALYZING_VERIFY_COPROPRIETES },
        { label: 'Ajout ASL, AFUL et immeubles tertiaires', done: addCoprosDone, route: ROUTES.ANALYZING_ADD_COPROPRIETES },
        { label: 'Adresses validées dans le module', done: addressDone, route: ROUTES.ANALYZING_ADD_ADDRESSES },
      ],
      status: computeStatus(step1AllDone, coproDone || addCoprosDone || addressDone, true),
      ctaRoute: step1CtaRoute,
    },
    {
      number: 2,
      title: 'Qualifiez vos cibles : SCI',
      description: 'Identifiez et qualifiez les contacts SCI et les cibles potentielles pour la gestion locative.',
      checklist: [
        { label: 'Qualification des SCI pour la gestion locative', done: sciTargetDone },
        { label: 'Qualification des SCI pour la transaction', done: sciTargetDone },
      ],
      status: computeStatus(sciTargetDone, false, true),
      ctaRoute: ROUTES.PROSPECTING_SCIS_REFERENT,
    },
    {
      number: 3,
      title: "Qualification des dates d'AG",
      description: 'Renseignez les dates des assemblées générales pour planifier les actions du référent.',
      checklist: [
        { label: 'Dernière AG renseignée', done: agDateDone },
        { label: 'Prochaine AG planifiée', done: agDateDone },
      ],
      status: computeStatus(agDateDone, false, true),
      ctaRoute: ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT,
    },
  ];

  const visio: StepCardConfig[] = [
    {
      number: 1,
      title: "Qualification de l'activité",
      description: 'Qualifiez votre activité de transaction immobilière et vos partenariats.',
      checklist: [
        { label: "Qualification de l'activité de transaction", done: transactionDone },
        { label: 'Partenariats déclarés', done: transactionDone },
      ],
      status: computeStatus(transactionDone, false, step1AllDone),
      ctaRoute: ROUTES.SETUP_TRANSACTION_QUALIFICATION,
    },
    {
      number: 2,
      title: 'Image de marque & Rapports',
      description: 'Personnalisez vos communications et la présentation de l\'entreprise en tête de vos rapports.',
      checklist: [
        { label: 'Nom commercial & logo', done: hasCommercialName && hasLogo },
        { label: 'Couleurs & modèle newsletter', done: hasColors && hasTemplate },
        { label: 'Référent mutation / location', done: hasReferent },
        { label: 'Présentation entreprise pour les rapports (optionnel)', done: reportDone },
      ],
      status: computeStatus(
        (hasCommercialName && hasLogo) && (hasColors && hasTemplate) && hasReferent,
        hasCommercialName || hasLogo || hasColors || hasTemplate || hasReferent || reportDone,
        step1AllDone && transactionDone,
      ),
      ctaRoute: ROUTES.SETUP_BRANDING,
    },
    {
      number: 3,
      title: 'Attribution des collaborateurs',
      description: 'Assignez vos collaborateurs par copropriété et configurez les alertes DPE.',
      checklist: [
        { label: 'Attribution des collaborateurs', done: collaboratorsDone, route: ROUTES.SETUP_ADD_COLLABORATORS },
        { label: 'Configuration des alertes emails', done: alertsDone, route: ROUTES.SETUP_CONFIGURE_NOTIFICATIONS },
      ],
      status: computeStatus(
        collaboratorsDone && alertsDone,
        collaboratorsDone || alertsDone,
        step1AllDone && transactionDone,
      ),
      ctaRoute: collaboratorsCtaRoute,
    },
    {
      number: 4,
      title: 'Configuration des mails',
      description: 'Associez les référents, configurez les SMTP et consultez l\u2019historique des envois.',
      checklist: [
        { label: 'Référents associés aux copropriétés', done: mailConfigDone },
        { label: 'Paramètres SMTP configurés et testés', done: mailConfigDone },
      ],
      status: computeStatus(
        mailConfigDone,
        false,
        step1AllDone && transactionDone && brandingComplete,
      ),
      ctaRoute: ROUTES.SETUP_MAIL_CONFIG,
    },
  ];

  return { afaire, visio };
}

const STATUS_CONFIG: Record<CardStatus, { label: React.ReactNode; className: string }> = {
  done: {
    label: <><CheckIcon className="inline h-3 w-3" /> TERMINÉE</>,
    className: 'bg-green-100 text-green-800',
  },
  'in-progress': { label: 'EN COURS', className: 'bg-orange-50 text-orange-700 border border-orange-200' },
  upcoming: { label: 'À FAIRE', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  disabled: { label: 'DÉSACTIVÉE', className: 'bg-gray-100 text-gray-400' },
};

function StatusBadge({ status }: { status: CardStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span className={classNames('inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold tracking-wide', config.className)}>
      {config.label}
    </span>
  );
}

function ChecklistItemRow({ item }: { item: ChecklistItem }) {
  const content = (
    <>
      <div
        className={classNames(
          'mt-0.5 flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full',
          item.done ? 'bg-green-100' : 'bg-gray-100',
        )}
      >
        {item.done ? (
          <CheckIcon className="h-2.5 w-2.5 text-green-600" />
        ) : (
          <div className="h-1.5 w-1.5 rounded-full border border-gray-400" />
        )}
      </div>
      <span className={classNames(item.route && 'group-hover:text-rgpd-blue group-hover:underline')}>
        {item.label}
      </span>
    </>
  );

  const baseClass = 'flex items-start gap-2 text-[12.5px] leading-snug text-slate-700';

  if (item.route) {
    return (
      <Link
        to={item.route}
        className={classNames(baseClass, 'group -mx-1 rounded px-1 py-0.5 transition-colors hover:bg-slate-50')}
      >
        {content}
      </Link>
    );
  }

  return <div className={baseClass}>{content}</div>;
}

function StepCard({ card }: { card: StepCardConfig }) {
  const isClickable = card.status !== 'disabled';
  const headerColor = STATUS_HEADER_COLORS[card.status];
  const ctaColor = STATUS_CTA_COLORS[card.status];

  const ctaClasses = classNames(
    'block w-full rounded-lg py-2.5 text-center text-[13px] font-bold text-white transition-colors',
    ctaColor,
  );

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      {/* Colored header */}
      <div className={classNames('flex items-center gap-3 px-5 py-4', headerColor)}>
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-white/20 text-[17px] font-extrabold text-white">
          {card.number}
        </div>
        <div className="flex-1 text-sm leading-tight font-bold text-white">{card.title}</div>
        {card.onTuto && (
          <button
            type="button"
            onClick={card.onTuto}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/30 bg-white/15 px-2.5 py-1 text-[11px] font-bold tracking-wide text-white transition-colors hover:bg-white/25"
            aria-label={`Voir le tutoriel : ${card.title}`}
          >
            <AcademicCapIcon className="h-3.5 w-3.5" />
            Tuto
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 px-5 py-4">
        <p className="text-xs leading-relaxed text-gray-500">{card.description}</p>
        <div className="flex flex-1 flex-col gap-2">
          {card.checklist.map((item) => (
            <ChecklistItemRow key={item.label} item={item} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-2.5 border-t border-gray-100 px-5 py-3.5">
        <StatusBadge status={card.status} />
        {isClickable && card.onAction ? (
          <button type="button" onClick={card.onAction} className={ctaClasses}>
            Accéder à l&apos;étape &rarr;
          </button>
        ) : isClickable && card.ctaRoute ? (
          <Link to={card.ctaRoute} className={ctaClasses}>
            Accéder à l&apos;étape &rarr;
          </Link>
        ) : (
          <button
            type="button"
            disabled
            className="w-full cursor-default rounded-lg bg-gray-200 py-2.5 text-center text-[13px] font-bold text-gray-400"
          >
            Terminez l&apos;étape précédente
          </button>
        )}
        {card.onMarkAsCompleted && card.status !== 'done' && (
          <button
            type="button"
            onClick={() => { void card.onMarkAsCompleted?.(); }}
            className="block w-full rounded-lg border border-green-700 bg-white py-2.5 text-center text-[13px] font-bold text-green-700 transition-colors hover:bg-green-700 hover:text-white"
          >
            Marquer comme terminé
          </button>
        )}
      </div>
    </div>
  );
}

function StepCardsGrid({ keyPrefix, cards }: { keyPrefix: string; cards: StepCardConfig[] }) {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
      {cards.map((card) => (
        <StepCard key={`${keyPrefix}-${card.number}`} card={card} />
      ))}
    </div>
  );
}

function ParametrageSetupTab() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch<AppDispatch>();

  const baseCards = useMemo(() => buildStepCards(company), [company]);

  const handleMarkAsCompleted = useCallback(
    async(key: CompanySetupStepsKey, label: string): Promise<void> => {
      if (!company || !jwt) { return; }
      await updateSetupState(company, jwt, key, dispatch);
      showToast('success', `${label} : étape marquée comme terminée`);
    },
    [company, jwt, dispatch],
  );

  const handleOpenTuto = useCallback((title: string) => {
    const pdfUrl = SETUP_TUTO_PDFS[title];
    if (!pdfUrl) { return; }
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const cards = useMemo<SetupCards>(() => ({
    afaire: baseCards.afaire.map((card) => {
      const hasTuto = Boolean(SETUP_TUTO_PDFS[card.title]);
      const withTuto: StepCardConfig = {
        ...card,
        onTuto: hasTuto ? () => handleOpenTuto(card.title) : undefined,
      };
      if (card.title === 'Qualifiez vos cibles : SCI') {
        return {
          ...withTuto,
          onMarkAsCompleted: () => handleMarkAsCompleted('sciTargetQualification', card.title),
        };
      }
      if (card.title === "Qualification des dates d'AG") {
        return {
          ...withTuto,
          onMarkAsCompleted: () => handleMarkAsCompleted('agDateQualification', card.title),
        };
      }
      return withTuto;
    }),
    visio: baseCards.visio,
  }), [baseCards, handleMarkAsCompleted, handleOpenTuto]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-800">Votre parcours de paramétrage</h2>
        <p className="text-[13px] font-medium text-rgpd-blue">Complétez les étapes pour profiter de votre Module Croissance</p>
      </div>

      <div className="flex flex-col gap-8">
        <FramedSection
          visual={SOLO_VISUAL}
          title="À faire"
          subtitle="Étapes à compléter de votre côté, à votre rythme."
        >
          <StepCardsGrid keyPrefix="afaire" cards={cards.afaire} />
        </FramedSection>

        <div className="relative flex items-center" aria-hidden="true">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="mx-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[10.5px] font-bold tracking-wide text-slate-500">
            <VideoCameraIcon className="h-3 w-3" />
            ÉTAPES TRAITÉES EN VISIO
          </span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <FramedSection
          visual={VISIO_VISUAL}
          title="Ensemble en visio"
          subtitle="À aborder lors du rendez-vous d'accompagnement avec votre interlocuteur Déclarations Juridiques."
        >
          <StepCardsGrid keyPrefix="visio" cards={cards.visio} />
        </FramedSection>
      </div>

      <div className="mt-8">
        <SubscriptionBanner company={company} jwt={jwt} />
      </div>
    </div>
  );
}

export default ParametrageSetupTab;
